/* eslint-disable no-console */
import { useEffect, useCallback, useRef } from "react";
import { Any, FEATURE_FLAGS } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import type { AuthStateProvider, AuthUser } from "./types";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { useApi } from "@/hooks/core/useApi";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useTonConnectUI, useTonWallet, useIsConnectionRestored } from "@tonconnect/ui-react";
import { useSnackbar } from "notistack";
import { create } from "zustand";

const localStorageKey = "cede-ton-connect-auth-token";
const payloadTTLMS = 1000 * 60 * 20; // 20 minutes

// Custom event for token expiration
const TON_TOKEN_EXPIRED_EVENT = "TonConnectTokenExpired";

interface TonConnectState {
  user: AuthUser | null;
  accessToken: string | null;
  wallet: string | null;
  ready: boolean;
  isInitialized: boolean;
}

const initialState: TonConnectState = {
  user: null,
  accessToken: null,
  wallet: null,
  ready: false,
  isInitialized: false,
};

type TonConnectStore = TonConnectState & {
  setState: (state: Partial<TonConnectState>) => void;
};

const useTonConnectStore = create<TonConnectStore>((set) => ({
  ...initialState,
  setState: (state) => set((prev) => ({ ...prev, ...state })),
}));

/**
 * Emit token expired event
 */
const emitTokenExpiredEvent = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(TON_TOKEN_EXPIRED_EVENT));
  }
};

/**
 * Hook to manage the TON Connect authentication
 */
export const useTonConnect: () => AuthStateProvider = () => {
  const { setState, ...state } = useTonConnectStore();
  const [tonConnectUI] = useTonConnectUI();
  const { enqueueSnackbar } = useSnackbar();
  const { initDataRaw } = useTelegramContext();

  /**
   * Reset authentication state
   */
  const reset = useCallback(() => {
    setState({
      ...initialState,
      ready: true, // Keep ready as true after reset to avoid loading state
      isInitialized: true,
    });
    localStorage.removeItem(localStorageKey);
  }, [setState]);

  /**
   * Handle token expiration
   */
  const onTokenExpired = useCallback(() => {
    enqueueSnackbar("Session expired. Please reconnect your wallet.", { variant: "warning" });
    reset();
  }, [reset, enqueueSnackbar]);

  /**
   * Get the access token for API requests - simplified without validation
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    // Just return the token - validation happens at API level and by the useSetupTon hook
    return state.accessToken;
  }, [state.accessToken]);

  /**
   * Login function - opens TON Connect modal
   */
  const login = useCallback(async () => {
    // This will be handled by the TON Connect UI component
    // The actual connection is managed by the TonConnectUIProvider
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      await tonConnectUI.disconnect();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to logout from TON Connect", error);
      enqueueSnackbar("Failed to logout from TON Connect", { variant: "error" });
    } finally {
      reset();
    }
  }, [reset, tonConnectUI]);

  /**
   * Initialize widget - not needed for TON Connect as it's handled by the provider
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const initializeWidget = useCallback((_container: HTMLElement) => {
    // TON Connect doesn't need manual widget initialization
    return () => {
      // Cleanup function
    };
  }, []);

  /**
   * Set up token expiration event listener
   */
  useEffect(() => {
    const handleTokenExpired = () => {
      onTokenExpired();
      tonConnectUI.disconnect();
    };

    window.addEventListener(TON_TOKEN_EXPIRED_EVENT, handleTokenExpired);

    return () => {
      window.removeEventListener(TON_TOKEN_EXPIRED_EVENT, handleTokenExpired);
    };
  }, [onTokenExpired, tonConnectUI]);

  return {
    ready: state.ready,
    authenticated: Boolean(state.user),
    user: state.user,
    login,
    logout,
    getAccessToken,
    initializeWidget,
    identityProvider: "ton",
    identityToken: initDataRaw,
    refreshIdentityToken: null,
  };
};

/**
 * Hook to initialize the TON Connect provider with authentication
 */
export const useSetupTon = () => {
  const isTonOn = useFeatureFlagIsOn(FEATURE_FLAGS.TON_CONNECT_HUB);
  const { setState } = useTonConnectStore();
  const apiHelper = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const interval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const tokenValidationInterval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const { initDataRaw } = useTelegramContext();

  /**
   * Internal token validation for periodic checks
   */
  const validateTokenInternal = useCallback(
    async (token: string): Promise<boolean> => {
      if (!apiHelper) {
        return false;
      }

      try {
        await apiHelper.getWithAuth(
          {
            identityToken: initDataRaw,
            getAccessToken: async () => token, // Direct token, no validation loop
            identityProvider: "ton",
            refreshIdentityToken: null,
          },
          API_ENDPOINT.HUB_USER_ME,
        );
        return true;
      } catch {
        // Emit token expired event instead of handling directly
        emitTokenExpiredEvent();
        return false;
      }
    },
    [apiHelper, initDataRaw],
  );

  // TON Connect UI hooks
  const wallet = useTonWallet();
  const connectionRestored = useIsConnectionRestored();
  const [tonConnectUI] = useTonConnectUI();

  /**
   * Generate payload from backend
   */
  const generatePayload = useCallback(async (): Promise<string | null> => {
    try {
      const response = await apiHelper.getOrThrow(API_ENDPOINT.TON_CONNECT_GENERATE_PAYLOAD);
      return response.payload;
    } catch {
      return null;
    }
  }, [apiHelper]);

  /**
   * Check proof with backend
   */
  const checkProof = useCallback(
    async (proof: Any, account: Any): Promise<string | null> => {
      try {
        const proofData = {
          address: account.address,
          network: account.chain,
          public_key: account.publicKey,
          proof: {
            timestamp: proof.timestamp,
            domain: proof.domain,
            signature: proof.signature,
            payload: proof.payload,
            state_init: account.walletStateInit,
          },
        };

        const response = await apiHelper.postOrThrow(API_ENDPOINT.TON_CONNECT_VALIDATE_PROOF, {
          bodyParams: proofData,
        });

        return response.accessToken;
      } catch {
        return null;
      }
    },
    [apiHelper],
  );

  /**
   * Refresh payload periodically
   */
  const refreshPayload = useCallback(async () => {
    if (!isTonOn || !initDataRaw) return; // Only in TMA
    tonConnectUI.setConnectRequestParameters({ state: "loading" });

    const payload = await generatePayload();
    if (!payload) {
      tonConnectUI.setConnectRequestParameters(null);
    } else {
      tonConnectUI.setConnectRequestParameters({
        state: "ready",
        value: { tonProof: payload },
      });
    }
  }, [isTonOn, initDataRaw, tonConnectUI, generatePayload]);

  /**
   * Handle authentication flow
   */
  useEffect(() => {
    if (!connectionRestored || !isTonOn || !initDataRaw) {
      return;
    }

    clearInterval(interval.current);

    if (!wallet) {
      // Check if we have a stored token - if so, don't clear it yet (wallet might be restoring)
      const storedToken = localStorage.getItem(localStorageKey);
      if (storedToken) {
        // Don't clear localStorage yet - give wallet time to restore
        setState({
          ...initialState,
          ready: true,
          isInitialized: true,
          accessToken: storedToken, // Keep the token
        });
        return;
      }

      // No wallet connected and no token - reset state and set up payload refresh
      localStorage.removeItem(localStorageKey);
      setState({
        ...initialState,
        ready: true,
        isInitialized: true,
      });

      refreshPayload();
      interval.current = setInterval(refreshPayload, payloadTTLMS);
      return;
    }

    // Check if we already have a token for this wallet
    const token = localStorage.getItem(localStorageKey);
    if (token) {
      setState({
        user: {
          id: wallet.account.address,
          wallet: {
            address: wallet.account.address,
          },
          defiAddress: wallet.account.address,
        },
        accessToken: token,
        wallet: wallet.account.address,
      });
      return;
    }

    // Process new connection with proof
    if (wallet.connectItems?.tonProof && "proof" in wallet.connectItems.tonProof) {
      checkProof(wallet.connectItems.tonProof.proof, wallet.account).then((accessToken) => {
        if (accessToken) {
          localStorage.setItem(localStorageKey, accessToken);

          setState({
            user: {
              id: wallet.account.address,
              wallet: {
                address: wallet.account.address,
              },
              defiAddress: wallet.account.address,
            },
            accessToken,
            wallet: wallet.account.address,
          });
        } else {
          enqueueSnackbar("Authentication failed. Please try again.", { variant: "error" });
          tonConnectUI.disconnect();
        }
      });
    } else {
      enqueueSnackbar("TON proof is required for authentication.", { variant: "error" });
      tonConnectUI.disconnect();
    }
  }, [
    wallet,
    connectionRestored,
    isTonOn,
    initDataRaw,
    refreshPayload,
    checkProof,
    tonConnectUI,
    setState,
    enqueueSnackbar,
  ]);

  /**
   * Set ready state when connection is restored
   */
  useEffect(() => {
    if (!isTonOn || !initDataRaw) return;

    if (connectionRestored) {
      setState({
        ready: true,
        isInitialized: true,
      });
    }
  }, [connectionRestored, isTonOn, initDataRaw, setState]);

  /**
   * Try to retrieve the token from the local storage on mount
   */
  useEffect(() => {
    if (!isTonOn || !initDataRaw) return;

    const storedToken = localStorage.getItem(localStorageKey);

    if (storedToken && wallet) {
      setState({
        user: {
          id: wallet.account.address,
          wallet: {
            address: wallet.account.address,
          },
          defiAddress: wallet.account.address,
        },
        accessToken: storedToken,
        wallet: wallet.account.address,
      });
    } else if (storedToken) {
      setState({
        accessToken: storedToken,
        // We'll get user info when wallet connects
      });
    }
  }, [isTonOn, initDataRaw, setState, wallet, connectionRestored]);

  /**
   * Periodic token validation - only run when user is authenticated
   */
  useEffect(() => {
    if (!isTonOn || !connectionRestored || !initDataRaw) return;

    const currentState = useTonConnectStore.getState();

    if (currentState.accessToken && currentState.user && apiHelper) {
      clearInterval(tokenValidationInterval.current);

      tokenValidationInterval.current = setInterval(async () => {
        const token = useTonConnectStore.getState().accessToken;
        if (!token) {
          clearInterval(tokenValidationInterval.current);
          return;
        }

        // Use internal validation to avoid circular dependency
        const isValid = await validateTokenInternal(token);
        if (!isValid) {
          emitTokenExpiredEvent();
          clearInterval(tokenValidationInterval.current);
        }
      }, 1000 * 30); // 30 seconds
    } else {
      clearInterval(tokenValidationInterval.current);
    }

    return () => {
      clearInterval(tokenValidationInterval.current);
    };
  }, [isTonOn, connectionRestored, initDataRaw, apiHelper, setState, enqueueSnackbar, wallet, validateTokenInternal]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearInterval(interval.current);
      clearInterval(tokenValidationInterval.current);
    };
  }, []);

  /**
   * Handle wallet disconnection
   */
  useEffect(() => {
    if (!isTonOn || !connectionRestored || !initDataRaw) return;

    // If wallet was connected but now is null, it means user disconnected
    const currentState = useTonConnectStore.getState();
    if (currentState.user && !wallet) {
      setState({
        ...initialState,
        ready: true,
        isInitialized: true,
      });
      localStorage.removeItem(localStorageKey);
    }
  }, [wallet, isTonOn, connectionRestored, initDataRaw, setState]);
};
